"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "../cx.ts";
import { ErrorText } from "../form/field.tsx";
import { useTranslate } from "../i18n.tsx";
import { Button } from "./button.tsx";
import { IconButton } from "./icon-button.tsx";

/**
 * The entrance and the exit.
 *
 * A `<dialog>` has three states, not two — closed, open, and the state an
 * opening one starts from — and `@starting-style` (`starting:`) is the only
 * thing that names the third. `display` and `overlay` transition with
 * `allow-discrete` because a dialog leaves the top layer the instant it
 * closes; without them the panel slides in and then simply vanishes.
 *
 * Every declaration is scoped to a state no other one can also match —
 * `not-open` against `open` — so none of this depends on the order Tailwind
 * happens to emit its utilities in.
 *
 * `motion-safe` gates the slide alone. A fade is not motion, and a panel that
 * appears with no transition at all reads as a rendering glitch.
 */
const MOTION =
  "transition-[opacity,translate,display,overlay] transition-discrete duration-250 " +
  "not-open:opacity-0 starting:open:opacity-0 " +
  "not-open:motion-safe:translate-x-[var(--drawer-slide)] " +
  "starting:open:motion-safe:translate-x-[var(--drawer-slide)] " +
  "backdrop:transition-[opacity,display,overlay] backdrop:transition-discrete " +
  "backdrop:duration-250 not-open:backdrop:opacity-0 starting:open:backdrop:opacity-0";

/** How long the exit above lasts. Kept beside the `duration-250` it mirrors. */
const EXIT_MS = 250;

/**
 * How an open drawer inside a `DrawerMount` learns that it is closing. The
 * alternative was an `open` prop threaded through all twenty-odd editors, none
 * of which have any use for it beyond handing it straight to the drawer they
 * render.
 */
const DrawerOpenContext = createContext<boolean | null>(null);

/**
 * Where a `FormDrawer`'s way out leads, for a form that was opened *over*
 * something rather than from a list.
 *
 * A record panel swaps itself for its editor in place, so the editor's Cancel
 * means "back to the record", not "close the drawer" — but the editor is
 * handed one `onClose` and uses it for both a cancel and a finished save. This
 * is how the panel says where a cancel goes without an `onCancel` threaded
 * through all twenty-odd editors, none of which would do anything with it but
 * hand it to the drawer they render. Unset — a form opened from a list, or a
 * create form — a cancel closes, exactly as before.
 */
const DrawerCancelContext = createContext<(() => void) | null>(null);

/**
 * Says that everything inside is a form standing in for something the reader
 * came from, and a cancel should go back to it. `RecordPanel` is the only
 * caller today; the delete confirmation it raises already behaves this way.
 */
export function DrawerCancelsTo({
  onCancel,
  children,
}: {
  onCancel: () => void;
  children: ReactNode;
}) {
  return <DrawerCancelContext.Provider value={onCancel}>{children}</DrawerCancelContext.Provider>;
}

/**
 * Which way it travels. `translate` is physical where `ms`/`me` are logical,
 * so the direction is resolved once here rather than in four class names.
 */
const SLIDE = {
  end: "ltr:[--drawer-slide:100%] rtl:[--drawer-slide:-100%]",
  start: "ltr:[--drawer-slide:-100%] rtl:[--drawer-slide:100%]",
};

/**
 * How wide the panel is allowed to get once there is a viewport to be wide in.
 * A drawer is full-width on a phone at every size — the constraint only starts
 * at `sm`.
 *
 * Three sizes rather than a free `max-w-*`: `sm` is the column of fields most
 * of these forms are, `md` is what a form with paired fields or a long body
 * needs, and `lg` is for the ones carrying a table of lines. A call site that
 * picks a width by hand is a fourth size nobody decided on.
 */
const WIDTHS = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-3xl",
};

export type DrawerSize = keyof typeof WIDTHS;

/**
 * What the panel is made of. A named prop rather than a `className` the caller
 * overrides, because that override cannot work: `cx` concatenates and does not
 * merge, so the panel's own `bg-surface` and a caller's `bg-chrome` both land
 * and the cascade decides between them. It picked `bg-surface` — which is how
 * both shells' navigation sheets came to draw `text-ink-inverse` on white and
 * hide their own title.
 */
const TONES = {
  /** A form or a record — every editor in the two consoles. */
  surface: "bg-surface text-ink-body",
  /** A navigation sheet, matching the sidebar it stands in for below `lg`. */
  chrome: "bg-chrome text-ink-inverse",
};

export type DrawerTone = keyof typeof TONES;

/**
 * An edit panel at the inline end of the screen, and a full-height sheet on a
 * phone.
 *
 * Built on `<dialog>` for the same reason `Modal` is: the platform gives
 * modality, focus containment, Escape and a backdrop, and a hand-rolled
 * version of those is where accessibility bugs live.
 *
 * `ms-auto me-0 my-0` is this component's version of `Modal`'s `m-auto`, and
 * it is load-bearing for the same reason — the UA centres an open `<dialog>`
 * with `margin: auto` and Tailwind's preflight resets that to 0. Using the
 * logical `ms`/`me` rather than `ml`/`mr` is also what puts the panel on the
 * left in a right-to-left locale without a second rule.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  closeLabel,
  children,
  footer,
  side = "end",
  size = "sm",
  tone = "surface",
}: {
  /** Omitted inside a `DrawerMount`, which supplies it. */
  open?: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Names the close button. App callers pass `t(...)`. */
  closeLabel: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Which edge it enters from, in logical terms. Navigation opens at the
      start, an edit panel at the end. */
  side?: "start" | "end";
  /** How wide the panel may grow above `sm`. */
  size?: DrawerSize;
  /** Surface for a form, chrome for a drawer that stands in for the sidebar. */
  tone?: DrawerTone;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const mounted = useContext(DrawerOpenContext);
  const isOpen = open ?? mounted ?? true;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) dialog.showModal();
    if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  // `showModal()` makes the page inert but does not stop it scrolling under
  // the backdrop, which is very visible beside a panel this tall.
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = previous;
    };
  }, [isOpen]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      className={cx(
        "my-0 h-dvh max-h-dvh w-full max-w-full rounded-none border-0 p-0",
        "shadow-drawer backdrop:bg-chrome/40",
        TONES[tone],
        WIDTHS[size],
        side === "end" ? "ms-auto me-0" : "me-auto ms-0",
        MOTION,
        SLIDE[side],
      )}
    >
      <div className="flex h-full flex-col">
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-current/10 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{title}</h2>
            {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
          </div>
          <IconButton icon="x" label={closeLabel} size="sm" onClick={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-line bg-surface-subtle px-5 py-4">{footer}</div>
        ) : null}
      </div>
    </dialog>
  );
}

/**
 * The shape almost every editor in the back office and the operator console
 * has: a column of fields, whatever went wrong at the bottom of it, and a
 * footer of cancel, save, and — where the record can be removed — a delete at
 * the inline start.
 *
 * It exists because that footer was written out by hand in thirty-odd files,
 * which is thirty-odd chances for the buttons to land in a different order, for
 * a save to stay live while a save is already in flight, or for a delete to sit
 * next to the button somebody is aiming at. The labels default to the shared
 * `action.*` messages rather than being passed at every call site; a screen
 * that needs its own wording ("Create merchant") passes one.
 *
 * View-only panels use `Drawer` directly — a footer with nothing but a close
 * button repeats the header's own.
 */
export function FormDrawer({
  open,
  onClose,
  title,
  description,
  size,
  onSubmit,
  submitting = false,
  disabled = false,
  destructive = false,
  submitLabel,
  submittingLabel,
  cancelLabel,
  closeLabel,
  deleteLabel,
  onDelete,
  secondaryAction,
  startAction,
  error,
  children,
}: {
  /** Omitted inside a `DrawerMount`, which supplies it. */
  open?: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  size?: DrawerSize;
  onSubmit: () => void;
  /** Holds the footer while the request is in flight, both buttons included. */
  submitting?: boolean;
  /** The form itself is not ready — an empty required field, a bad amount. */
  disabled?: boolean;
  /** Colours the submit as the danger it is: an invalidation, a void. */
  destructive?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  closeLabel?: string;
  deleteLabel?: string;
  /** Given only where the record can be removed from the panel that edits it. */
  onDelete?: () => void;
  /**
   * A second way out of the form, beside the primary one — the stock count
   * that can be saved without being applied. It sits next to the submit
   * because it is an outcome, not a cancel.
   */
  secondaryAction?: ReactNode;
  /**
   * An action that acts on the form rather than closing it — adding a line to
   * a list of them. It shares the inline start with the delete, which is where
   * the eye goes last.
   */
  startAction?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  const t = useTranslate();

  // Where the ways out of this form lead. Inside a record panel that is back to
  // the record; everywhere else it is `onClose`, which is what it has always
  // been. A finished save still calls `onClose` itself, so saving closes and
  // cancelling steps back — the two things the one callback could not tell
  // apart before.
  const cancel = useContext(DrawerCancelContext) ?? onClose;

  return (
    <Drawer
      open={open}
      onClose={cancel}
      title={title}
      description={description}
      size={size}
      closeLabel={closeLabel ?? t("action.close")}
      footer={
        <div className="flex items-center justify-end gap-2">
          {onDelete || startAction ? (
            <div className="me-auto flex items-center gap-2">
              {onDelete ? (
                <Button variant="danger-ghost" size="sm" onClick={onDelete} disabled={submitting}>
                  {deleteLabel ?? t("action.delete")}
                </Button>
              ) : null}
              {startAction}
            </div>
          ) : null}
          <Button variant="secondary" size="sm" onClick={cancel} disabled={submitting}>
            {cancelLabel ?? t("action.cancel")}
          </Button>
          {secondaryAction}
          <Button
            variant={destructive ? "danger" : "primary"}
            size="sm"
            loading={submitting}
            onClick={onSubmit}
            disabled={disabled}
          >
            {submitting
              ? (submittingLabel ?? t("action.saving"))
              : (submitLabel ?? t("action.save"))}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {children}
        <ErrorText>{error}</ErrorText>
      </div>
    </Drawer>
  );
}

/**
 * Keeps a drawer mounted long enough to leave.
 *
 * `Drawer` has a complete exit — `not-open:` against `open`, with `display`
 * and `overlay` on `allow-discrete` so the panel stays in the top layer while
 * it slides. None of it runs at a call site that renders its editor as
 * `{editing ? <Editor /> : null}`, which is nearly all of them: closing sets
 * the state to null, React takes the `<dialog>` out of the document in the
 * same commit, and there is no element left on screen to transition. The panel
 * does not slide out, it stops existing. The shells' navigation drawers are
 * the ones that animate both ways today, and the only difference is that they
 * stay mounted and pass `open`.
 *
 * So the latch belongs here rather than in each page's `useState`: `value`
 * going null is what closes the drawer, and the last non-null value is held
 * for the length of the exit, because that record is what the panel goes on
 * rendering while it slides. Every way out of a form arrives the same way —
 * the close button, Escape, cancel, and the `onClose()` an editor calls once
 * its save has landed all end at the page state this is watching.
 *
 * `key` is load-bearing. An editor reads its initial form state from the
 * record it is handed, and unmounting is what resets it today — hold one
 * mounted across a close and the last edit turns up in the next open. Bumping
 * the key each time the drawer opens restores exactly the remount that the
 * conditional render used to give for free.
 */
export function DrawerMount<T>({
  value,
  children,
}: {
  /**
   * The page's own state for what the drawer is showing, falsy when closed —
   * the record being edited, or the bare `true` of a panel that creates one.
   */
  value: T | null | undefined | false;
  children: (value: T) => ReactNode;
}) {
  const open = value !== null && value !== undefined && value !== false;
  const [held, setHeld] = useState<T | null>(open ? value : null);
  const [wasOpen, setWasOpen] = useState(open);
  const [instance, setInstance] = useState(0);

  if (open && value !== held) setHeld(value);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setInstance((n) => n + 1);
  }

  useEffect(() => {
    if (open || held === null) return;
    const timer = setTimeout(() => setHeld(null), EXIT_MS);
    return () => clearTimeout(timer);
  }, [open, held]);

  if (held === null) return null;
  return (
    <DrawerOpenContext.Provider key={instance} value={open}>
      {children(held)}
    </DrawerOpenContext.Provider>
  );
}
